<template>
  <div>
    <div v-if="errorCaused"></div>
    <div v-else-if="cmsFetching">
      <Skeleton />
    </div>
    <div v-else>
      <!--
        Render the CMS content branch only when validated content actually
        exists. Otherwise default to the PLP. During a client-side navigation
        `fetchOnNavigation` does not await the category fetch before paint, so
        `isPLP` is briefly `false` for a valid PLP URL while the category
        resolves. Keying the content branch off `!isPLP` (instead of `content`)
        rendered an empty `SafelyHydrate` during that window; defaulting to the
        PLP restores optimistic rendering. A genuine not-found route throws a
        404 from the script (`errorCaused`) before reaching this branch.
      -->
      <SafelyHydrate v-if="content">
        <component :is="content.component" :blok="content" />
      </SafelyHydrate>

      <ProductListingPage v-else />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { fetchOnNavigation } from '../utils/loading'
import useCatchAllPrefetch from '../composables/useCatchAllPrefetch'
import { definePageMeta } from '#imports'
import { createError } from '#app/composables/error'
import { useRoute } from '#app/composables/router'
import { useStoryRedirect } from '../composables/useStoryRedirect'

const route = useRoute()

useRouteCache((helper) => {
  helper.setMaxAge(3600).setCacheable()
})

// Use the shared composable - same instance/keys as layout prefetch
const {
  categoryData,
  cmsData,
  cmsFetching,
  cmsExcluded,
  isPLP,
  isContentPage,
  content,
  path,
  refreshCategory,
  fetchCmsContent,
} = useCatchAllPrefetch()

const errorCaused = ref(false)

const { redirectIfNeeded } = useStoryRedirect()

// ECOM-2385: a resolved ContentPage with an active `redirectUrl` forwards the
// visitor to the target (SSR 302, no new history entry). Reads the field off the
// validated CMS content; a no-op for PLPs, AdvantagePages, and pages without the
// field. Returns true when it redirected so callers can skip render.
const redirectGuard = () =>
  redirectIfNeeded((content.value as any)?.redirectUrl)

const tryGetCmsData = async () => {
  await fetchCmsContent(path.value)

  if (cmsExcluded.value || !cmsData.value) {
    errorCaused.value = true
    throw createError({ statusCode: 404, statusMessage: 'Not found' })
  }
}

onMounted(async () => {
  if (await redirectGuard()) {
    return
  }

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem('last-plp', route.path)
  }
})

await fetchOnNavigation(async () => {
  // Check if category data was already prefetched by layout
  if (categoryData.value) {
    return
  }

  // Category not prefetched - fetch now
  await refreshCategory()

  if (categoryData.value) {
    return
  }

  // Check if CMS data was already prefetched by layout
  if (cmsData.value) {
    // Only skip fetching if the prefetched CMS data is actually renderable by this page.
    if (isContentPage.value) {
      // Redirect must run here on the prefetched path, before the early return,
      // or it would silently no-op on the common SSR case. The return is
      // unconditional by design: a prefetched content page either redirects
      // (navigateTo, fire-and-forget like scheduleGuard) or renders — both stop
      // fetching here. Do NOT gate the return on redirectGuard()'s result, or a
      // non-redirecting content page would fall through to the 404 below.
      await redirectGuard()
      return
    }

    errorCaused.value = true
    throw createError({ statusCode: 404, statusMessage: 'Not found' })
  }

  // CMS not prefetched - fetch now
  await tryGetCmsData()

  if (isContentPage.value && (await redirectGuard())) {
    return
  }

  if (!isPLP.value && !isContentPage.value) {
    errorCaused.value = true
    throw createError({ statusCode: 404, statusMessage: 'Not found' })
  }
}, 'cms-content-page-fetch')

defineOptions({ name: 'CategoryOrContentPage' })
// Nuxt 4 forbids multiple definePageMeta calls AND treats it as a compile-time macro
// (reactive values like content.value would not be evaluated). Using static value
// "category" to match the prior default behavior; downstream consumers can branch on
// the actual content/category data at runtime.
definePageMeta({ pageType: 'category' })
</script>
